import React, { useState } from 'react';
import { Plus, Upload, FileText, Trash2, Edit2, Play, CheckCircle, XCircle, AlertCircle, Brain, Database, TestTube, Settings, Save, X, Download } from 'lucide-react';
import './AIAgentPage.scss';

const SAMPLE_KNOWLEDGE_BANKS = [
    {
        id: 1,
        name: 'Product Documentation',
        description: 'Complete product features and specifications',
        documents: 12,
        size: '2.4 MB',
        uploadDate: 'Jan 10, 2026',
        status: 'Active'
    },
    {
        id: 2,
        name: 'Pricing & Plans',
        description: 'Pricing information, packages, and discount policies',
        documents: 5,
        size: '850 KB',
        uploadDate: 'Jan 8, 2026',
        status: 'Active'
    },
    {
        id: 3,
        name: 'FAQ Database',
        description: 'Frequently asked questions and troubleshooting',
        documents: 28,
        size: '1.8 MB',
        uploadDate: 'Jan 5, 2026',
        status: 'Active'
    }
];

const SAMPLE_AI_RULES = [
    {
        id: 1,
        name: 'Product Inquiry Handler',
        trigger: 'Questions about products or features',
        knowledgeBank: 'Product Documentation',
        model: 'GPT-4',
        temperature: 0.7,
        maxTokens: 500,
        status: 'Active',
        responses: 234
    },
    {
        id: 2,
        name: 'Pricing Support',
        trigger: 'Pricing and cost questions',
        knowledgeBank: 'Pricing & Plans',
        model: 'Claude-3',
        temperature: 0.5,
        maxTokens: 400,
        status: 'Active',
        responses: 156
    },
    {
        id: 3,
        name: 'General FAQ Assistant',
        trigger: 'Common questions and support',
        knowledgeBank: 'FAQ Database',
        model: 'GPT-3.5',
        temperature: 0.6,
        maxTokens: 300,
        status: 'Inactive',
        responses: 89
    }
];

const SAMPLE_TEST_SCENARIOS = [
    {
        id: 1,
        name: 'Pricing Inquiry from Arjun',
        query: 'आपका एंटरप्राइज प्लान की कीमत क्या है? (What is your enterprise plan pricing?)',
        expectedResponse: 'हमारा Enterprise प्लान ₹24,999 प्रति माह से शुरू होता है जिसमें unlimited users, priority support, और advanced analytics शामिल हैं। क्या आप एक डेमो शेड्यूल करना चाहेंगे?',
        models: ['GPT-4', 'Claude-3', 'GPT-3.5'],
        status: 'Completed',
        lastRun: 'Jan 12, 2026',
        results: [
            { model: 'GPT-4', score: 92, response: 'हमारा Enterprise प्लान ₹24,999 प्रति माह में उपलब्ध है, जिसमें unlimited users, priority support, और advanced analytics शामिल हैं। मैं आपके लिए एक व्यक्तिगत डेमो शेड्यूल कर सकता हूँ।' },
            { model: 'Claude-3', score: 88, response: 'Enterprise प्लान की कीमत ₹24,999 प्रति माह है और इसमें unlimited users, priority support, और advanced analytics features शामिल हैं।' },
            { model: 'GPT-3.5', score: 75, response: 'Enterprise प्लान की कीमत ₹24,999 प्रति माह है और सभी features शामिल हैं।' }
        ]
    },
    {
        id: 2,
        name: 'Product Feature Question from Priya',
        query: 'क्या आपका सॉफ्टवेयर Salesforce के साथ integrate होता है? (Does your software integrate with Salesforce?)',
        expectedResponse: 'हाँ! हमारे पास Salesforce के साथ native integration है जो bi-directional sync करता है। आप contacts, leads, और activities को automatically sync कर सकते हैं।',
        models: ['GPT-4', 'Claude-3', 'GPT-3.5'],
        status: 'Completed',
        lastRun: 'Jan 11, 2026',
        results: [
            { model: 'GPT-4', score: 95, response: 'बिल्कुल! हमारे पास Salesforce के साथ native integration है जिसमें bi-directional sync capabilities हैं contacts, leads, और activities के लिए।' },
            { model: 'Claude-3', score: 90, response: 'हाँ, हम Salesforce integration को support करते हैं और automatically contacts, leads, और activities को दोनों दिशाओं में sync करते हैं।' },
            { model: 'GPT-3.5', score: 82, response: 'हाँ, Salesforce integration उपलब्ध है contacts और leads syncing के साथ।' }
        ]
    },
    {
        id: 3,
        name: 'Support Request from Vikram',
        query: 'मुझे अपने अकाउंट सेटअप में मदद चाहिए (I need help with my account setup)',
        expectedResponse: 'मुझे आपकी अकाउंट सेटअप में मदद करने में खुशी होगी! मैं आपको हमारी support team से जोड़ता हूँ जो तुरंत आपकी मदद कर सकते हैं।',
        models: ['GPT-4', 'Claude-3'],
        status: 'Draft',
        lastRun: null,
        results: []
    }
];

const AI_MODELS = [
    { id: 'gpt-4', name: 'GPT-4', provider: 'OpenAI', description: 'Most capable model, best for complex reasoning' },
    { id: 'gpt-3.5', name: 'GPT-3.5 Turbo', provider: 'OpenAI', description: 'Fast and cost-effective for most tasks' },
    { id: 'claude-3', name: 'Claude 3', provider: 'Anthropic', description: 'Excellent for detailed analysis and writing' },
    { id: 'claude-2', name: 'Claude 2', provider: 'Anthropic', description: 'Reliable and accurate responses' }
];

function AIAgentPage() {
    const [activeTab, setActiveTab] = useState('knowledge');
    const [knowledgeBanks, setKnowledgeBanks] = useState(SAMPLE_KNOWLEDGE_BANKS);
    const [aiRules, setAiRules] = useState(SAMPLE_AI_RULES);
    const [testScenarios, setTestScenarios] = useState(SAMPLE_TEST_SCENARIOS);
    const [showKnowledgeForm, setShowKnowledgeForm] = useState(false);
    const [showRuleForm, setShowRuleForm] = useState(false);
    const [showTestForm, setShowTestForm] = useState(false);
    const [selectedTest, setSelectedTest] = useState(null);
    const [runningTest, setRunningTest] = useState(null);

    const handleDeleteKnowledgeBank = (id) => {
        setKnowledgeBanks(knowledgeBanks.filter(kb => kb.id !== id));
    };

    const handleDeleteRule = (id) => {
        setAiRules(aiRules.filter(rule => rule.id !== id));
    };

    const handleToggleRuleStatus = (id) => {
        setAiRules(aiRules.map(rule => 
            rule.id === id ? { ...rule, status: rule.status === 'Active' ? 'Inactive' : 'Active' } : rule
        ));
    };

    const handleRunTest = (testId) => {
        setRunningTest(testId);
        setTimeout(() => {
            setTestScenarios(testScenarios.map(test => 
                test.id === testId ? { ...test, status: 'Completed', lastRun: 'Just now' } : test
            ));
            setRunningTest(null);
        }, 2000);
    };

    const handleDeleteTest = (id) => {
        setTestScenarios(testScenarios.filter(test => test.id !== id));
        if (selectedTest?.id === id) {
            setSelectedTest(null);
        }
    };

    return (
        <div className="ai-agent-page">
            <div className="ai-agent-header">
                <div>
                    <h1 className="ai-agent-title">AI Agent Configuration</h1>
                    <p className="ai-agent-desc">Configure AI-powered auto-responses with knowledge banks and test scenarios</p>
                </div>
                {activeTab === 'knowledge' && (
                    <button className="ai-agent-btn-new" onClick={() => setShowKnowledgeForm(true)}>
                        <Plus size={18} />
                        <span>New Knowledge Bank</span>
                    </button>
                )}
                {activeTab === 'rules' && (
                    <button className="ai-agent-btn-new" onClick={() => setShowRuleForm(true)}>
                        <Plus size={18} />
                        <span>New AI Rule</span>
                    </button>
                )}
                {activeTab === 'testing' && (
                    <button className="ai-agent-btn-new" onClick={() => setShowTestForm(true)}>
                        <Plus size={18} />
                        <span>New Test Scenario</span>
                    </button>
                )}
            </div>

            <div className="ai-agent-tabs">
                <button 
                    className={`ai-agent-tab ${activeTab === 'knowledge' ? 'active' : ''}`}
                    onClick={() => setActiveTab('knowledge')}
                >
                    <Database size={16} />
                    Knowledge Banks
                </button>
                <button 
                    className={`ai-agent-tab ${activeTab === 'rules' ? 'active' : ''}`}
                    onClick={() => setActiveTab('rules')}
                >
                    <Brain size={16} />
                    AI Rules
                </button>
                <button 
                    className={`ai-agent-tab ${activeTab === 'testing' ? 'active' : ''}`}
                    onClick={() => setActiveTab('testing')}
                >
                    <TestTube size={16} />
                    Testing & Comparison
                </button>
            </div>

            <div className="ai-agent-content">
                {activeTab === 'knowledge' && (
                    <div className="knowledge-section">
                        <div className="knowledge-grid">
                            {knowledgeBanks.map((kb) => (
                                <div key={kb.id} className="knowledge-card">
                                    <div className="knowledge-card-header">
                                        <div className="knowledge-card-icon">
                                            <Database size={24} />
                                        </div>
                                        <div className="knowledge-card-actions">
                                            <button className="knowledge-edit-btn">
                                                <Edit2 size={14} />
                                            </button>
                                            <button className="knowledge-delete-btn" onClick={() => handleDeleteKnowledgeBank(kb.id)}>
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                    <h3 className="knowledge-card-title">{kb.name}</h3>
                                    <p className="knowledge-card-desc">{kb.description}</p>
                                    <div className="knowledge-card-stats">
                                        <div className="knowledge-stat">
                                            <FileText size={14} />
                                            <span>{kb.documents} documents</span>
                                        </div>
                                        <div className="knowledge-stat">
                                            <span>{kb.size}</span>
                                        </div>
                                    </div>
                                    <div className="knowledge-card-footer">
                                        <span className={`knowledge-status ${kb.status.toLowerCase()}`}>
                                            {kb.status}
                                        </span>
                                        <span className="knowledge-date">Updated {kb.uploadDate}</span>
                                    </div>
                                </div>
                            ))}
                            
                            <div className="knowledge-card knowledge-card-add" onClick={() => setShowKnowledgeForm(true)}>
                                <Upload size={48} />
                                <h3>Upload New Knowledge Bank</h3>
                                <p>Add documents, PDFs, or text files to train your AI agent</p>
                            </div>
                        </div>

                        <div className="knowledge-sidebar">
                            <div className="knowledge-help">
                                <h3>Knowledge Banks</h3>
                                <p>Upload documents, PDFs, or text files containing information your AI agent can use to respond to customers.</p>
                                <ul>
                                    <li>Support multiple file formats (PDF, DOCX, TXT, MD)</li>
                                    <li>Automatic text extraction and indexing</li>
                                    <li>Update anytime without retraining</li>
                                    <li>Track usage and relevance</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'rules' && (
                    <div className="rules-section">
                        <div className="rules-list">
                            {aiRules.map((rule) => (
                                <div key={rule.id} className="rule-card">
                                    <div className="rule-header">
                                        <div className="rule-info">
                                            <h3 className="rule-name">{rule.name}</h3>
                                            <p className="rule-trigger">Trigger: {rule.trigger}</p>
                                            <div className="rule-meta">
                                                <span className="rule-kb">
                                                    <Database size={14} />
                                                    {rule.knowledgeBank}
                                                </span>
                                                <span className="rule-model">
                                                    <Brain size={14} />
                                                    {rule.model}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="rule-controls">
                                            <button 
                                                className={`rule-status-btn ${rule.status === 'Active' ? 'active' : ''}`}
                                                onClick={() => handleToggleRuleStatus(rule.id)}
                                            >
                                                {rule.status}
                                            </button>
                                            <button className="rule-edit-btn">
                                                <Edit2 size={16} />
                                            </button>
                                            <button className="rule-delete-btn" onClick={() => handleDeleteRule(rule.id)}>
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="rule-settings">
                                        <div className="rule-setting">
                                            <label>Temperature:</label>
                                            <span>{rule.temperature}</span>
                                        </div>
                                        <div className="rule-setting">
                                            <label>Max Tokens:</label>
                                            <span>{rule.maxTokens}</span>
                                        </div>
                                        <div className="rule-setting">
                                            <label>Responses:</label>
                                            <span className="rule-responses">{rule.responses}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="rules-sidebar">
                            <div className="rules-help">
                                <h3>AI Response Rules</h3>
                                <p>Create rules that determine when and how your AI agent responds to customer messages.</p>
                                <h4>Configuration Options:</h4>
                                <ul>
                                    <li><strong>Temperature:</strong> Controls randomness (0-1)</li>
                                    <li><strong>Max Tokens:</strong> Response length limit</li>
                                    <li><strong>Knowledge Bank:</strong> Information source</li>
                                    <li><strong>Model Selection:</strong> Choose AI provider</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'testing' && (
                    <div className="testing-section">
                        <div className="testing-main">
                            <h3 className="testing-title">Test Scenarios</h3>
                            <div className="testing-list">
                                {testScenarios.map((test) => (
                                    <div 
                                        key={test.id} 
                                        className={`test-card ${selectedTest?.id === test.id ? 'selected' : ''}`}
                                        onClick={() => setSelectedTest(test)}
                                    >
                                        <div className="test-header">
                                            <div className="test-info">
                                                <h4 className="test-name">{test.name}</h4>
                                                <p className="test-query">{test.query}</p>
                                            </div>
                                            <div className="test-controls">
                                                <button 
                                                    className="test-run-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRunTest(test.id);
                                                    }}
                                                    disabled={runningTest === test.id}
                                                >
                                                    {runningTest === test.id ? <Settings size={16} className="spin" /> : <Play size={16} />}
                                                </button>
                                                <button 
                                                    className="test-delete-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteTest(test.id);
                                                    }}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="test-meta">
                                            <span className={`test-status ${test.status.toLowerCase()}`}>
                                                {test.status === 'Completed' && <CheckCircle size={12} />}
                                                {test.status === 'Draft' && <AlertCircle size={12} />}
                                                {test.status}
                                            </span>
                                            <span className="test-models">{test.models.length} models</span>
                                            {test.lastRun && <span className="test-date">Last run: {test.lastRun}</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="testing-details">
                            {selectedTest ? (
                                <>
                                    <div className="test-details-header">
                                        <h3>{selectedTest.name}</h3>
                                        <button className="test-close-btn" onClick={() => setSelectedTest(null)}>
                                            <X size={20} />
                                        </button>
                                    </div>

                                    <div className="test-details-section">
                                        <h4>Test Query</h4>
                                        <div className="test-query-box">{selectedTest.query}</div>
                                    </div>

                                    <div className="test-details-section">
                                        <h4>Expected Response</h4>
                                        <div className="test-expected-box">{selectedTest.expectedResponse}</div>
                                    </div>

                                    {selectedTest.results.length > 0 && (
                                        <div className="test-details-section">
                                            <h4>Model Comparison Results</h4>
                                            <div className="test-results">
                                                {selectedTest.results.map((result, idx) => (
                                                    <div key={idx} className="test-result-card">
                                                        <div className="test-result-header">
                                                            <span className="test-result-model">{result.model}</span>
                                                            <span className={`test-result-score ${result.score >= 90 ? 'excellent' : result.score >= 80 ? 'good' : 'fair'}`}>
                                                                Score: {result.score}%
                                                            </span>
                                                        </div>
                                                        <div className="test-result-response">
                                                            {result.response}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {selectedTest.status === 'Draft' && (
                                        <div className="test-empty-results">
                                            <TestTube size={48} />
                                            <p>Run this test to see model comparison results</p>
                                            <button 
                                                className="test-run-full-btn"
                                                onClick={() => handleRunTest(selectedTest.id)}
                                            >
                                                <Play size={18} />
                                                Run Test
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="test-details-empty">
                                    <TestTube size={64} />
                                    <h3>Select a test scenario</h3>
                                    <p>Choose a test from the list to view details and results</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AIAgentPage;
